import React, { ButtonHTMLAttributes, FC } from 'react';
import { config, useSpring } from '@react-spring/web';
import { t } from 'ttag';
import { animated } from '@react-spring/web';
import styled from '@xstyled/styled-components';
import PauseIcon from '~/assets/icons/pause.svg';
import PlayIcon from '~/assets/icons/play.svg';
import SpeakerIcon from '~/training/onboarding/assets/speaker.svg';
import { drillAnimation } from '~/ui/animation';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';

const Button = styled.button`
  background: ${p => p.theme.colors.accentDark};
  min-width: 50px;
  height: 50px;
  border-radius: 25px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  &:focus {
    outline: 3px solid var(--color-accent-dk);
    outline-offset: 3px;
  }
  &:focus::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: 50%;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid white;
    pointer-events: none;
  }
  @media ((max-width: 580px) and (max-height: 300px)) {
    min-width: 25px;
    height: 28px;
  }
`;

export const Play = styled(PlayIcon)`
  margin-left: 4px;
  z-index: hud;
`;

const Speaker = styled(SpeakerIcon)`
  max-width: 30px;
  max-height: 30px;
  fill: #fff;
`;

const DrillBoxAnimation = styled.div`
  width: 60px;
  height: 60px;
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: accentDark;
  border-radius: 50%;
  opacity: 0.5;
  @media ((max-width: 580px) and (max-height: 300px)) {
    width: 35px;
    height: 35px;
  }
`;

const selector = (s: TrainingState) => select(s, 'visualFeedback');

const PlayWrapper = () => {
  const { visualFeedback } = useTrainingStore(selector);
  return (
    <>
      <Play id={'playBtnSvg'} />
      {visualFeedback && <DrillBoxAnimation />}
    </>
  );
};

export const Pause = styled(PauseIcon)``;

const Progress = animated(styled.svg<{ progress?: number }>`
  width: 50px;
  height: 50px;
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  stroke-width: 2px;
  fill: none;
  stroke: #fff;
  stroke-linecap: round;
  stroke-linejoin: round;
  visibility: ${p => (p.progress ? 'visible' : 'hidden')};
  @media ((max-width: 580px) and (max-height: 300px)) {
    display: none;
  }
`);

const titles = {
  playing: t`Pause`,
  ready: t`Play`,
  paused: t`Play`,
  speaker: t`Play`
};

const arialabels = {
  playing: t`Pause the instructions`,
  ready: t`Play the instructions`,
  paused: t`Play the instructions`,
  speaker: t`Play the instructions`
};

export const PlayButton: FC<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    state: 'playing' | 'paused' | 'ready' | 'speaker';
    progress?: number;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
> = ({
  children,
  state,
  progress,
  title,
  'aria-label': ariaLabel,
  ...props
}) => {
  const { x } = useSpring({
    from: { x: 0 },
    x: progress || 0,
    delay: 200,
    config: config.molasses
  });

  return (
    <Button
      {...props}
      title={title ?? titles[state]}
      aria-label={ariaLabel ?? arialabels[state]}
    >
      {' '}
      {(state === 'paused' || state === 'ready') && (
        <PlayWrapper key={`play_btn_${Math.random()}`} />
      )}
      {state === 'playing' && <Pause />}
      {state === 'speaker' && <Speaker />}
      <Progress
        progress={progress}
        strokeDasharray={144}
        strokeDashoffset={x.to(x => (1 - x) * 144)}
        className="progressSvg"
      >
        <circle cx="25" cy="25" r="23" />
      </Progress>
    </Button>
  );
};
